import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  useTheme,
  alpha,
  FormControl,
  TextField,
  Stack,
} from '@mui/material';

import { filter } from 'lodash';
import BackButton from '../backButton';
import { UserListHead } from '../../sections/@dashboard/user';
import ModalDelete from '../ModalDelete';
import Label from '../Label';

const OPERATOR_ADMINS_TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
];

export default function MoreOperator({
  deleteModal,
  handleCloseMoreOperators,
  operator,
  handleDelete,
  handleOpenDeleteModal,
  handleCloseDeleteModal,
  handleOpenPageOperator,
  role,
  filteredUsers,
  zones,
}) {
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const theme = useTheme();

  // ---- Reusable Styles (same from MoreUsers) ----
  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const editButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
    },
  };

  const removeButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.error.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.error.main, 0.85),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
    },
  };

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  };
  // ---- Sorting Helpers ----
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredUsers.map((n) => n.email);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  function descendingComparator(a, b, sortBy) {
    if (b[sortBy] < a[sortBy]) return -1;
    if (b[sortBy] > a[sortBy]) return 1;
    return 0;
  }

  function getComparator(sortOrder, sortBy) {
    return sortOrder === 'desc'
      ? (a, b) => descendingComparator(a, b, sortBy)
      : (a, b) => -descendingComparator(a, b, sortBy);
  }

  function applySortFilter(array, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const sortResult = comparator(a[0], b[0]);
      if (sortResult !== 0) return sortResult;
      return a[1] - b[1];
    });
    if (query) {
      return filter(
        array,
        (_user) => _user.firstName.toLowerCase().indexOf(query.toLowerCase()) !== -1,
      );
    }
    return stabilizedThis.map((el) => el[0]);
  }

  const filteredUsersSorted = applySortFilter(
    filteredUsers,
    getComparator(order, orderBy),
    '',
  );

  const emptyRows = page > 0
    ? Math.max(0, (1 + page) * rowsPerPage - filteredUsersSorted.length)
    : 0;

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <BackButton back={handleCloseMoreOperators} type="operator" />
      </Box>

      {/* Title Section */}
      <Box
        sx={{
          mt: 1,
          pb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
        }}
      >
        <Typography variant="h3">{operator.operatorName}</Typography>
        <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
          View all details of
          {' '}
          {operator.operatorName}
        </Typography>
      </Box>

      {/* Operator Details */}
      <Box sx={{
        padding: 2, borderTop: '1px solid', borderColor: theme.palette.grey[300], display: 'flex',
      }}
      >
        <Box sx={{ width: '70%', mt: 2 }}>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>Operator ID:</Typography>
            <TextField disabled sx={{ flexGrow: 1 }} value={operator.operatorID} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>Name:</Typography>
            <TextField disabled sx={{ flexGrow: 1 }} value={operator.operatorName} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>Assigned Zone(s):</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flexGrow: 1, alignItems: 'center' }}>
              {zones
                ?.filter((zone) => operator.assignedZones?.includes(zone._id))
                .map((zone) => (
                  <Label key={zone._id} variant="soft" color="primary">
                    {zone.name}
                  </Label>
                ))}
              {(!operator.assignedZones || operator.assignedZones.length === 0) && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No Zones Assigned
              </Typography>
              )}
            </Stack>
          </FormControl>
        </Box>
      </Box>

      {/* Operator Admins */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: theme.palette.grey[300],
          pt: 3,
        }}
      >
        <Typography variant="h3">
          {operator.operatorName}
          {' '}
          Admins
        </Typography>

        <TableContainer sx={{ mt: 2 }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={OPERATOR_ADMINS_TABLE_HEAD}
              rowCount={filteredUsersSorted.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {filteredUsersSorted.length > 0 ? (
                filteredUsersSorted
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row._id}>
                      <TableCell>
                        {row.firstName}
                        {' '}
                        {row.lastName}
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.phone}</TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={OPERATOR_ADMINS_TABLE_HEAD.length + 1}
                    align="center"
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: theme.palette.grey[500], my: 2 }}
                    >
                      No admins assigned.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'right',
          borderTop: '1px solid',
          borderColor: theme.palette.grey[300],
          pt: 3,
        }}
      >
        <Button sx={buttonStyle} onClick={() => handleCloseMoreOperators()}>
          <Typography sx={{ fontWeight: 'fontWeightMedium' }}>Cancel</Typography>
        </Button>

        {(role === 900 || role === 101) && (
          <>
            <Button sx={removeButtonStyle} onClick={handleOpenDeleteModal}>
              <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                Delete Operator
              </Typography>
            </Button>
            <Button
              sx={editButtonStyle}
              onClick={() => handleOpenPageOperator(operator)}
            >
              <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                Edit
              </Typography>
            </Button>
          </>
        )}
      </Box>

      {deleteModal && (
        <ModalDelete
          deleteModal={deleteModal}
          handleDelete={handleDelete}
          handleClose={handleCloseDeleteModal}
          data={operator}
        />
      )}
    </>
  );
}
